/**
 * Werkorder Formulier - Nieuw/Bewerken
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { werkordersService, WerkorderCreate } from '../../services/werkorders.service';
import { relationsService, Relation } from '../../services/relations.service';
import { botenService, BootDetail } from '../../services/boten.service';
import { werknemersService, Werknemer } from '../../services/werknemers.service';
import { useAuth } from '../../context/AuthContext';
import Toast from '../../components/Toast';
import './WerkorderDetail.css';

interface WerkorderFormProps {
  werkorderId?: number;
}

const WerkorderForm = ({ werkorderId }: WerkorderFormProps) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isEditMode = !!werkorderId;

  const [formData, setFormData] = useState<WerkorderCreate>({
    relatie_id: 0,
    boot_id: undefined,
    prioriteit: 'NORMAAL',
    werkorder_datum: new Date().toISOString().split('T')[0],
    geplande_startdatum: undefined,
    geplande_einddatum: undefined,
    toegewezen_aan_werknemer_id: undefined,
    onderwerp: '',
    omschrijving: '',
    werkzaamheden: '',
    interne_notities: '',
    geschatte_uren: undefined,
    uurloon: undefined,
    btw_percentage: 21,
    actief: true,
  });

  const [relaties, setRelaties] = useState<Relation[]>([]);
  const [boten, setBoten] = useState<BootDetail[]>([]);
  const [werknemers, setWerknemers] = useState<Werknemer[]>([]);
  const [loading, setLoading] = useState(false);
  const [relatiesLoaded, setRelatiesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Searchable dropdown state
  const [relatieSearch, setRelatieSearch] = useState('');
  const [relatieDropdownOpen, setRelatieDropdownOpen] = useState(false);

  useEffect(() => {
    loadOptions();
    if (isEditMode && werkorderId) {
      loadWerkorder(werkorderId);
    }
  }, [isEditMode, werkorderId]);

  useEffect(() => {
    if (formData.relatie_id > 0) {
      loadBotenVoorRelatie(formData.relatie_id);
    }
  }, [formData.relatie_id]);

  // Effect om relatie search veld in te vullen nadat relaties geladen zijn
  useEffect(() => {
    if (formData.relatie_id > 0 && relatiesLoaded && relaties.length > 0) {
      const relatie = relaties.find(r => r.id === formData.relatie_id);
      if (relatie && !relatieSearch) {
        setRelatieSearch(getRelatieNaam(relatie));
      }
    }
  }, [formData.relatie_id, relatiesLoaded, relaties, relatieSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-dropdown')) {
        setRelatieDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOptions = async () => {
    try {
      // Laad klanten
      const relatiesData = await relationsService.getAll({
        actief: true,
        limit: 100,
        type: 'Klant'
      });
      setRelaties(relatiesData.items);
      setRelatiesLoaded(true);

      // Laad werknemers
      const werknemersData = await werknemersService.getAll({ actief: true, limit: 100 });
      setWerknemers(werknemersData.items);
    } catch (err: any) {
      setError(err.message || 'Kon opties niet laden');
    }
  };

  const loadWerkorder = async (id: number) => {
    try {
      setLoading(true);
      const werkorder = await werkordersService.getById(id);
      setFormData({
        relatie_id: werkorder.relatie_id,
        boot_id: werkorder.boot_id,
        prioriteit: werkorder.prioriteit,
        werkorder_datum: werkorder.werkorder_datum,
        geplande_startdatum: werkorder.geplande_startdatum,
        geplande_einddatum: werkorder.geplande_einddatum,
        toegewezen_aan_werknemer_id: werkorder.toegewezen_aan_werknemer_id,
        onderwerp: werkorder.onderwerp,
        omschrijving: werkorder.omschrijving || '',
        werkzaamheden: werkorder.werkzaamheden || '',
        interne_notities: werkorder.interne_notities || '',
        geschatte_uren: werkorder.geschatte_uren,
        uurloon: werkorder.uurloon,
        btw_percentage: werkorder.btw_percentage,
        actief: werkorder.actief,
      });

      // Vul relatie search veld alleen als relaties al geladen zijn
      if (relatiesLoaded && relaties.length > 0) {
        const relatie = relaties.find(r => r.id === werkorder.relatie_id);
        if (relatie) {
          setRelatieSearch(getRelatieNaam(relatie));
        }
      }
    } catch (err: any) {
      setError(err.message || 'Kon werkorder niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadBotenVoorRelatie = async (relatieId: number) => {
    try {
      const botenData = await botenService.getAll({ eigenaar_id: relatieId });
      setBoten(Array.isArray(botenData) ? botenData : (botenData as any).items || []);
    } catch (err) {
      console.error('Kon boten niet laden:', err);
      setBoten([]);
    }
  };

  const getRelatieNaam = (relatie: Relation): string => {
    if (relatie.bedrijfsnaam === 'Particulier' && relatie.achternaam) {
      return `${relatie.voornaam || ''} ${relatie.tussenvoegsel || ''} ${relatie.achternaam}`.trim();
    }
    return relatie.bedrijfsnaam;
  };

  const handleRelatieSelect = (relatie: Relation) => {
    setFormData({ ...formData, relatie_id: relatie.id, boot_id: undefined });
    setRelatieSearch(getRelatieNaam(relatie));
    setRelatieDropdownOpen(false);
  };

  const filteredRelaties = relaties.filter(r =>
    getRelatieNaam(r).toLowerCase().includes(relatieSearch.toLowerCase())
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number'
        ? (value === '' ? undefined : parseFloat(value))
        : value === '' ? undefined : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.relatie_id) {
      setError('Selecteer een klant');
      return;
    }

    if (!formData.onderwerp) {
      setError('Vul een onderwerp in');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode && werkorderId) {
        await werkordersService.update(werkorderId, formData);
        setSuccess('Werkorder bijgewerkt!');
      } else {
        const newWerkorder = await werkordersService.create(formData);
        setSuccess('Werkorder aangemaakt!');
        setTimeout(() => {
          navigate(`/werkorders/${newWerkorder.id}`);
        }, 1500);
        return;
      }

      setTimeout(() => {
        navigate('/werkorders');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  // Check permissions
  const canCreate = hasPermission('werkorders_aanmaken');
  const canEdit = hasPermission('werkorders_wijzigen');
  
  if (!isEditMode && !canCreate) {
    return (
      <div className="werkorder-form">
        <div className="page-header">
          <h1>Toegang Geweigerd</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: '#dc3545' }}>
          <p>U heeft geen toestemming om werkorders aan te maken.</p>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/werkorders')}
            style={{ marginTop: '1rem' }}
          >
            Terug naar Werkorders
          </button>
        </div>
      </div>
    );
  }

  if (isEditMode && !canEdit) {
    return (
      <div className="werkorder-form">
        <div className="page-header">
          <h1>Alleen Lezen</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: '#856404' }}>
          <p>U kunt deze werkorder alleen bekijken, niet bewerken.</p>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate(`/werkorders/${werkorderId}`)}
            style={{ marginTop: '1rem' }}
          >
            Bekijk Werkorder
          </button>
        </div>
      </div>
    );
  }

  if (loading && isEditMode) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="werkorder-form">
      <div className="page-header">
        <h1>{isEditMode ? 'Werkorder Bewerken' : 'Nieuwe Werkorder'}</h1>
      </div>

      <Toast message={error} type="error" onClose={() => setError(null)} />
      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit}>
        {/* Klant Selectie */}
        <section className="form-section">
          <h2>Klant Informatie</h2>
          <div className="form-grid">
            <div className="form-group span-2">
              <label>Klant <span className="required">*</span></label>
              <div className="searchable-dropdown">
                <input
                  type="text"
                  value={relatieSearch}
                  onChange={(e) => {
                    setRelatieSearch(e.target.value);
                    setRelatieDropdownOpen(true);
                  }}
                  onFocus={() => setRelatieDropdownOpen(true)}
                  placeholder="Zoek klant..."
                  autoComplete="off"
                />
                {relatieDropdownOpen && (
                  <div className="dropdown-list">
                    {!relatiesLoaded ? (
                      <div className="dropdown-item" style={{ color: '#6c757d', cursor: 'default' }}>
                        Laden...
                      </div>
                    ) : filteredRelaties.length === 0 ? (
                      <div className="dropdown-item" style={{ color: '#6c757d', cursor: 'default' }}>
                        Geen klanten gevonden
                      </div>
                    ) : (
                      filteredRelaties.slice(0, 10).map(relatie => (
                        <div
                          key={relatie.id}
                          className="dropdown-item"
                          onClick={() => handleRelatieSelect(relatie)}
                        >
                          {getRelatieNaam(relatie)}
                          {relatie.plaats && <span className="dropdown-item-sub"> - {relatie.plaats}</span>}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {boten.length > 0 && (
              <div className="form-group">
                <label>Boot</label>
                <select
                  name="boot_id"
                  value={formData.boot_id || ''}
                  onChange={handleChange}
                >
                  <option value="">Geen boot geselecteerd</option>
                  {boten.map(boot => (
                    <option key={boot.id} value={boot.id}>
                      {boot.bootnaam || boot.registratienummer || `Boot ${boot.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </section>

        {/* Werkorder Details */}
        <section className="form-section">
          <h2>Werkorder Details</h2>
          <div className="form-grid">
            <div className="form-group span-2">
              <label>Onderwerp <span className="required">*</span></label>
              <input
                type="text"
                name="onderwerp"
                value={formData.onderwerp}
                onChange={handleChange}
                placeholder="Kort omschrijving van de werkzaamheden"
                required
              />
            </div>

            <div className="form-group">
              <label>Prioriteit</label>
              <select
                name="prioriteit"
                value={formData.prioriteit}
                onChange={handleChange}
              >
                <option value="LAAG">Laag</option>
                <option value="NORMAAL">Normaal</option>
                <option value="HOOG">Hoog</option>
                <option value="SPOED">Spoed</option>
              </select>
            </div>

            <div className="form-group">
              <label>Werkorder Datum</label>
              <input
                type="date"
                name="werkorder_datum"
                value={formData.werkorder_datum}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Geplande Startdatum</label>
              <input
                type="date"
                name="geplande_startdatum"
                value={formData.geplande_startdatum || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Geplande Einddatum</label>
              <input
                type="date"
                name="geplande_einddatum"
                value={formData.geplande_einddatum || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Toegewezen aan</label>
              <select
                name="toegewezen_aan_werknemer_id"
                value={formData.toegewezen_aan_werknemer_id || ''}
                onChange={handleChange}
              >
                <option value="">Niet toegewezen</option>
                {werknemers.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.voornaam} {w.tussenvoegsel ? w.tussenvoegsel + ' ' : ''}{w.achternaam}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Geschatte Uren</label>
              <input
                type="number"
                name="geschatte_uren"
                value={formData.geschatte_uren || ''}
                onChange={handleChange}
                step="0.5"
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Uurloon (€ per uur)</label>
              <input
                type="number"
                name="uurloon"
                value={formData.uurloon || ''}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="Laat leeg voor standaard uurloon"
              />
              <small className="form-help">
                Specifiek uurloon voor deze werkorder. Laat leeg om standaard bedrijfsuurloon te gebruiken.
              </small>
            </div>
          </div>
        </section>

        {/* Omschrijving */}
        <section className="form-section">
          <h2>Omschrijving</h2>
          <div className="form-group">
            <label>Omschrijving</label>
            <textarea
              name="omschrijving"
              value={formData.omschrijving || ''}
              onChange={handleChange}
              rows={3}
              placeholder="Algemene omschrijving..."
            />
          </div>

          <div className="form-group">
            <label>Werkzaamheden</label>
            <textarea
              name="werkzaamheden"
              value={formData.werkzaamheden || ''}
              onChange={handleChange}
              rows={5}
              placeholder="Gedetailleerde beschrijving van de uit te voeren werkzaamheden..."
            />
          </div>

          <div className="form-group">
            <label>Interne Notities</label>
            <textarea
              name="interne_notities"
              value={formData.interne_notities || ''}
              onChange={handleChange}
              rows={3}
              placeholder="Interne opmerkingen (niet zichtbaar voor klant)..."
            />
          </div>
        </section>

        {/* Acties */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/werkorders')}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Bezig...' : isEditMode ? 'Opslaan' : 'Werkorder Aanmaken'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WerkorderForm;
